import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { workApiFetch } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import { ACTIVE_PAGE_REFRESH_INTERVAL_MS } from "../lib/activePageRefresh";
import type { WorkOrderId } from "../lib/workIds";

const timeEntrySchema = z.object({
  id: z.string().uuid(),
  user: z
    .object({
      id: z.number().int(),
      name: z.string(),
      initials: z.string(),
    })
    .optional(),
  workDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  durationMinutes: z.number().int().nonnegative(),
  note: z.string().nullable(),
});

const responseSchema = z.object({
  timeEntries: z.array(timeEntrySchema),
  totalDurationMinutes: z.number().int().nonnegative(),
});

export type WorkOrderTimeEntry = z.infer<typeof timeEntrySchema>;
export type WorkOrderTimeEntriesResponse = z.infer<typeof responseSchema>;

async function fetchWorkOrderTimeEntries(
  customerId: string,
  facilityId: string,
  workOrderId: WorkOrderId,
  completionReview: boolean,
) {
  const review = completionReview ? "?review=completion" : "";
  const response = await workApiFetch(
    `/v1/customers/${encodeURIComponent(customerId)}/facilities/${encodeURIComponent(facilityId)}/work-orders/${encodeURIComponent(workOrderId)}/time-entries${review}`,
  );
  if (!response.ok) {
    throw new Error(`Failed to load time entries (${response.status}).`);
  }
  return responseSchema.parse(await response.json());
}

export function useWorkOrderTimeEntriesQuery<
  TData = WorkOrderTimeEntriesResponse,
>(
  workOrderId: WorkOrderId,
  options?: {
    completionReview?: boolean;
    enabled?: boolean;
    select?: (data: WorkOrderTimeEntriesResponse) => TData;
  },
) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const customerId = selectedCustomer?.id;
  const facilityId = selectedFacility?.id;

  return useQuery({
    queryKey: [
      "work-order-time-entries",
      customerId,
      facilityId,
      workOrderId,
      options?.completionReview ?? false,
    ],
    queryFn: () => {
      if (!customerId || !facilityId) {
        throw new Error("A customer and facility must be selected.");
      }
      return fetchWorkOrderTimeEntries(
        customerId,
        facilityId,
        workOrderId,
        options?.completionReview ?? false,
      );
    },
    enabled:
      Boolean(customerId && facilityId && workOrderId) &&
      (options?.enabled ?? true),
    refetchInterval: ACTIVE_PAGE_REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    select: options?.select,
    meta: { errorMessage: "Failed to load Work Order time entries." },
  });
}
